import * as React from "react";
import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/Combobox";
import { cn } from "@/lib/utils";
import { fetchOrgUsersForCc, OrgUserLite } from "../api";

/** Cc picker restricted to real org users (fetched once, from
 *  fetchOrgUsersForCc - UserStore's synced local cache, not free-typed
 *  email addresses - this replaces an earlier free-text version per
 *  explicit feedback that Cc must be picked from the organisation, not
 *  typed). Value/onChange stay plain email strings (matching
 *  approveAndNotifyFinance's ccEmails: string[] contract, api.ts) - this
 *  component only changes HOW those emails get added, resolving each
 *  pick's displayName for the chip instead of showing the raw address.
 *  excludeEmail (the To recipient) is filtered out of the picker's own
 *  options so a Manager can't accidentally Cc the same person the email
 *  is already going To. */
export function OrgUserCcPicker({
  value,
  onChange,
  excludeEmail,
  disabled,
  className
}: {
  value: string[];
  onChange: (value: string[]) => void;
  excludeEmail?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const [users, setUsers] = React.useState<OrgUserLite[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchOrgUsersForCc()
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  const byEmail = React.useMemo(() => {
    const map = new Map<string, OrgUserLite>();
    for (const u of users) {
      if (u.email) map.set(u.email, u);
    }
    return map;
  }, [users]);

  const options = React.useMemo(
    () =>
      users
        .filter(
          (u) =>
            !!u.email && u.email !== excludeEmail && !value.includes(u.email!)
        )
        .map((u) => ({
          value: u.email as string,
          label: u.displayName || (u.email as string),
          sublabel: u.displayName ? (u.email as string) : undefined
        })),
    [users, excludeEmail, value]
  );

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className={cn("space-y-2", className)}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((email, i) => {
            const user = byEmail.get(email);
            return (
              <Badge
                key={`${email}-${i}`}
                variant="secondary"
                className="gap-1 font-normal"
              >
                {user?.displayName || email}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    aria-label={`Remove ${user?.displayName || email}`}
                    className="rounded-full opacity-70 hover:opacity-100 focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      )}
      <Combobox
        value=""
        onChange={(email) => {
          if (email) onChange([...value, email]);
        }}
        options={options}
        placeholder={loading ? "Loading…" : "Add a person to Cc…"}
        searchPlaceholder="Search by name or email…"
        emptyText={loading ? "Loading…" : "No matching users."}
        disabled={disabled || loading}
      />
    </div>
  );
}
